/**
 * PricingPage.jsx — Página pública de planos VUKZ (/pricing)
 * ==========================================================
 * Modelo comercial: Basic / Basic+ / Pro / Elite (4 famílias × S/M/L/XL).
 * Fonte canônica: GET /billing/public/plans (via PricingSection →
 * publicPlansService). NENHUM preço hardcoded aqui.
 *
 * READ-ONLY: nada aqui cria assinatura ou toca billing. CTA = contato.
 * Visual: preto/branco/neutro, compatível com VukzHomePage.
 * Idioma: useSportsLang (persistido em localStorage 'vukz_sports_lang'),
 * sincronizado com a escolha PT/EN da homepage via botões locais.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useSportsLang } from '../../contexts/SportsLangContext';
import SEOHead from '../../components/seo/SEOHead';
import PricingSection from '../../components/landing/PricingSection';
import { ArrowRight } from 'lucide-react';

const V = {
  bg: '#ffffff',
  text: '#111111',
  textSoft: '#555555',
  textMute: '#8a8a8a',
  accent: '#111111',
  border: '#e6e6e6',
  cardBg: '#fafafa',
};

const T = {
  pt: {
    seoTitle: 'VUKZ — Planos',
    seoDesc: 'Planos Basic, Basic+, Pro e Elite da VUKZ — gestão esportiva AI-first para equipes de natação.',
    heroTitle: 'Planos para cada equipe',
    heroSub: 'Quatro planos, quatro tamanhos. Comece com os perfis que sua equipe precisa e evolua conforme o clube cresce.',
    capacityTitle: 'Como funcionam os tamanhos',
    capacityDesc: 'Cada plano tem quatro faixas de capacidade (S, M, L e XL) definidas pelo número de atletas. Os perfis de staff incluídos dependem do plano — e é possível adicionar atletas e profissionais extras conforme a necessidade.',
    faqTitle: 'Perguntas frequentes',
    faq: [
      { q: 'Qual a diferença entre os planos?', a: 'Cada plano adiciona perfis de staff: Basic (treinador + atleta), Basic+ (adiciona preparação física), Pro (adiciona nutrição) e Elite (equipe completa com gestão).' },
      { q: 'Posso mudar de plano depois?', a: 'Sim. É possível ajustar plano e tamanho conforme a evolução da equipe. Fale com a equipe VUKZ.' },
      { q: 'Como funciona a contratação?', a: 'O primeiro passo é falar com a equipe VUKZ para ativar o ambiente do seu clube.' },
    ],
    ctaTitle: 'Pronto para ativar seu clube?',
    ctaBtn: 'Solicitar acesso',
    backHome: 'Início',
    login: 'Entrar',
    footer: {
      desc: 'Gestão esportiva AI-first para equipes de natação.',
      privacy: 'Privacidade', terms: 'Termos', sla: 'SLA', contact: 'Contato',
      privacyTo: '/privacidade', termsTo: '/termos', slaTo: '/sla',
    },
    homeTo: '/pt',
  },
  en: {
    seoTitle: 'VUKZ — Plans',
    seoDesc: 'VUKZ Basic, Basic+, Pro and Elite plans — AI-first sports management for swimming teams.',
    heroTitle: 'Plans for every team',
    heroSub: 'Four plans, four sizes. Start with the roles your team needs and grow as your club grows.',
    capacityTitle: 'How sizes work',
    capacityDesc: 'Each plan has four capacity bands (S, M, L and XL) defined by the number of athletes. The staff roles included depend on the plan — and you can add extra athletes and professionals as needed.',
    faqTitle: 'Frequently asked questions',
    faq: [
      { q: 'What is the difference between plans?', a: 'Each plan adds staff roles: Basic (coach + athlete), Basic+ (adds physical training), Pro (adds nutrition) and Elite (full team with management).' },
      { q: 'Can I change plans later?', a: 'Yes. Plan and size can be adjusted as your team evolves. Talk to the VUKZ team.' },
      { q: 'How do I subscribe?', a: 'The first step is talking to the VUKZ team to activate your club environment.' },
    ],
    ctaTitle: 'Ready to activate your club?',
    ctaBtn: 'Request access',
    backHome: 'Home',
    login: 'Login',
    footer: {
      desc: 'AI-first sports management for swimming teams.',
      privacy: 'Privacy', terms: 'Terms', sla: 'SLA', contact: 'Contact',
      privacyTo: '/en/privacy', termsTo: '/en/terms', slaTo: '/en/sla',
    },
    homeTo: '/',
  },
};

const Container = ({ children, className = '' }) => (
  <div className={`max-w-6xl mx-auto px-6 ${className}`}>{children}</div>
);

const PricingPage = () => {
  const { lang, setLang } = useSportsLang();
  const l = lang === 'en' ? 'en' : 'pt';
  const t = T[l];

  return (
    <div style={{ backgroundColor: V.bg, color: V.text }} className="min-h-screen font-sans antialiased">
      <SEOHead title={t.seoTitle} description={t.seoDesc} path="/pricing" lang={l} />

      {/* ── NAVBAR (compatível com a homepage) ── */}
      <nav
        className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b"
        style={{ borderColor: V.border }}
        role="navigation"
        aria-label={l === 'en' ? 'Main navigation' : 'Navegação principal'}
      >
        <Container className="h-16 flex items-center justify-between gap-4">
          <Link to={t.homeTo} className="flex items-center gap-3 flex-shrink-0" aria-label="VUKZ">
            <img src="/vukz-logo.jpeg" alt="VUKZ" className="h-10 w-auto" />
          </Link>

          <div className="flex items-center gap-3">
            {/* Seletor de idioma */}
            <div className="flex items-center rounded-lg border overflow-hidden" style={{ borderColor: V.border }} aria-label={l === 'en' ? 'Language' : 'Idioma'}>
              <button
                type="button"
                onClick={() => setLang('en')}
                className="px-3 py-1.5 text-xs font-semibold transition-colors"
                style={l === 'en' ? { backgroundColor: V.accent, color: '#fff' } : { color: V.textSoft }}
                aria-pressed={l === 'en'}
                lang="en"
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLang('pt')}
                className="px-3 py-1.5 text-xs font-semibold transition-colors"
                style={l === 'pt' ? { backgroundColor: V.accent, color: '#fff' } : { color: V.textSoft }}
                aria-pressed={l === 'pt'}
                lang="pt-BR"
              >
                PT
              </button>
            </div>

            <Link
              to={t.homeTo}
              className="hidden sm:inline-block text-sm font-medium px-3 py-2 rounded-lg transition-opacity hover:opacity-70"
              style={{ color: V.textSoft }}
            >
              {t.backHome}
            </Link>
            <Link
              to="/login"
              className="text-sm font-semibold px-3 py-2 rounded-lg transition-opacity hover:opacity-70"
              style={{ color: V.text }}
            >
              {t.login}
            </Link>
            <Link
              to="/pre-cadastro"
              className="text-sm font-bold px-4 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: V.accent }}
            >
              {t.ctaBtn}
            </Link>
          </div>
        </Container>
      </nav>

      <main className="pt-28">
        {/* ── HERO ── */}
        <section className="pb-4">
          <Container className="text-center max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight">{t.heroTitle}</h1>
            <p className="text-base sm:text-lg mt-5 leading-relaxed" style={{ color: V.textSoft }}>
              {t.heroSub}
            </p>
          </Container>
        </section>

        {/* ── PLANOS (fonte canônica: /billing/public/plans) ── */}
        <PricingSection lang={l} showError />

        {/* ── CAPACIDADE ── */}
        <section className="py-14 border-t" style={{ borderColor: V.border, backgroundColor: V.cardBg }}>
          <Container className="max-w-3xl text-center">
            <h2 className="text-2xl font-black tracking-tight">{t.capacityTitle}</h2>
            <p className="text-sm mt-4 leading-relaxed" style={{ color: V.textSoft }}>
              {t.capacityDesc}
            </p>
          </Container>
        </section>

        {/* ── FAQ ── */}
        <section className="py-16">
          <Container className="max-w-3xl">
            <h2 className="text-2xl font-black tracking-tight text-center mb-8">{t.faqTitle}</h2>
            <div className="space-y-4">
              {t.faq.map((f) => (
                <div key={f.q} className="p-5 rounded-2xl border" style={{ borderColor: V.border }}>
                  <h3 className="text-sm font-bold" style={{ color: V.text }}>{f.q}</h3>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: V.textSoft }}>{f.a}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── CTA FINAL ── */}
        <section className="py-16" style={{ backgroundColor: V.text }}>
          <Container className="max-w-2xl text-center">
            <h2 className="text-3xl font-black text-white tracking-tight">{t.ctaTitle}</h2>
            <Link
              to="/pre-cadastro"
              className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-xl font-bold text-base transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#ffffff', color: V.text }}
            >
              {t.ctaBtn} <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </Container>
        </section>

        {/* ── FOOTER (compatível com a homepage) ── */}
        <footer className="py-8 border-t" style={{ borderColor: V.border, backgroundColor: V.bg }} role="contentinfo">
          <Container>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" style={{ color: V.textMute }}>
              <div className="text-center sm:text-left">
                <span className="font-bold" style={{ color: V.text }}>VUKZ</span>
                <p className="text-xs mt-1">{t.footer.desc}</p>
                <p className="text-xs mt-2">© {new Date().getFullYear()} VUKZ.</p>
              </div>
              <nav aria-label="Footer" className="flex flex-wrap justify-center gap-x-6 gap-y-2">
                <Link to={t.homeTo} className="hover:underline">{t.backHome}</Link>
                <Link to="/login" className="hover:underline">{t.login}</Link>
                <Link to={t.footer.privacyTo} className="hover:underline">{t.footer.privacy}</Link>
                <Link to={t.footer.termsTo} className="hover:underline">{t.footer.terms}</Link>
                <Link to={t.footer.slaTo} className="hover:underline">{t.footer.sla}</Link>
                <Link to="/contato" className="hover:underline">{t.footer.contact}</Link>
              </nav>
            </div>
          </Container>
        </footer>
      </main>
    </div>
  );
};

export default PricingPage;
